import type { TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueBy, validAges, splitSCNames } from '../../utils/deals';
import { RDOTS, SUB_REGIONS } from '../../utils/regions';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { TrendChart } from '../charts/TrendChart';
import { useDeals } from '../../context/DealsContext';
interface Props {
  state: TabState & {
    status: string;
  };
  onDrillRegion: (reg: string, sub: string) => void;
}
export function VPPanel({
  state,
  onDrillRegion
}: Props) {
  const {
    deals
  } = useDeals();
  const all = getFiltered('vp', state, deals);
  const ip = all.filter(d => d.status === 'In Progress');
  const cls = all.filter(d => d.status === 'Closed');
  const onh = all.filter(d => d.status === 'On Hold');
  const can = all.filter(d => d.status === 'Cancelled');
  const scs = new Set(ip.flatMap(d => splitSCNames(d.sc))).size;
  const pipelineOppGnarr = sumUniqueBy(all, d => d.opp_gnarr);
  const ages = validAges(all);
  const titleLbl = state.sub && state.sub !== 'All' ? state.sub : state.r;
  const crumb = state.sub && state.sub !== 'All';

  // Region/sub breakdown
  const showSub = state.r !== 'Global' && !!SUB_REGIONS[state.r];
  const regMap: Record<string, {
    ip: number;
    gnarr: number;
    closed: number;
    onhold: number;
    book: number;
    parent: string;
    cancelled: number;
  }> = {};
  all.forEach(d => {
    const key = showSub ? d.sub_region : d.region;
    if (!regMap[key]) regMap[key] = {
      ip: 0,
      gnarr: 0,
      closed: 0,
      onhold: 0,
      book: 0,
      parent: d.region
    };
    regMap[key].book++;
    if (d.status === 'In Progress') {
      regMap[key].ip++;
      regMap[key].gnarr += d.gnarr;
    } else if (d.status === 'Closed') regMap[key].closed++;else if (d.status === 'On Hold') regMap[key].onhold++;else if (d.status === 'Cancelled') regMap[key].cancelled++;
  });
  const maxIP = Math.max(...Object.values(regMap).map(r => r.ip), 1);
  const ALLOWED_REGIONS = new Set(['AMER', 'EMEA', 'JAPAC', '']);
  const regRows = Object.entries(regMap).filter(([reg]) => showSub || ALLOWED_REGIONS.has(reg)).sort((a, b) => b[1].ip - a[1].ip);

  // Book status bar — "In Progress" uses Adobe red
  const total = ip.length + cls.length + onh.length + can.length || 1;
  const segs = [{
    l: 'Closed',
    v: cls.length,
    c: '#34d399'
  }, {
    l: 'In Progress',
    v: ip.length,
    c: '#fa0f00'
  }, {
    l: 'On Hold',
    v: onh.length,
    c: '#d4a017'
  }, {
    l: 'Cancelled',
    v: can.length,
    c: '#f87171'
  }];

  // Close quarter grid
  const cqMap: Record<string, {
    n: number;
    g: number;
  }> = {};
  ip.forEach(d => {
    if (!cqMap[d.close_q]) cqMap[d.close_q] = {
      n: 0,
      g: 0
    };
    cqMap[d.close_q].n++;
    cqMap[d.close_q].g += d.gnarr;
  });
  const cqShow = Object.keys(cqMap).filter(q => q.includes('2026') || q === '2027-Q1').sort().slice(0, 3);
  const qColors = ['#d4a017', '#fa0f00', '#34d399'];
  return <div data-id="element-475" data-mp="31zwik5zjqf2k">
      {/* Metrics */}
      <div className="mrow" style={{
      gridTemplateColumns: 'repeat(5, 1fr)'
    }} data-id="element-476" data-mp="3619r4nu0wwd6">
       <MetricCard label="Total requests" value={ip.length + cls.length} sub={`Closed + In progress `} colorClass="c-green" data-id="element-477" data-mp="35z5d3d2eugz6" />
       <MetricCard label="Closed requests" value={cls.length} colorClass="c-green" data-id="element-478" data-mp="3frbe1can48qi" />
        <MetricCard label="In-progress requests" value={ip.length} sub={`${scs} active SCs`} colorClass="c-blue" data-id="element-479" data-mp="3ftfj3xk9zdsd" />
        <MedianAgeCard ages={ages} data-id="element-480" data-mp="3ppii4ujtywb3" />
        <MetricCard label="GNARR Influenced" value={fmtG(pipelineOppGnarr)} sub={`Closed & In-progress requests`} colorClass="c-green" data-id="element-481" data-mp="3pqk5nyi19b9p" />
      </div>

      {/* Trend chart */}
      <SectionHeader title={<>{titleLbl} — request volume &amp; GNARR by Created Quarter{crumb && <span className="region-crumb" data-id="element-483" data-mp="1zc9vnbsg8ubi">{state.sub}</span>}</>} data-id="element-482" data-mp="1vbpzobjm3wqy" />
      <TrendChart region={state.r} sub={state.sub} status={state.status} deals={deals} data-id="element-484" data-mp="142mr6b9cl0hb" />

      {/* Region breakdown table */}
      <SectionHeader title={<>Business impact by region — {titleLbl}{crumb && <span className="region-crumb" data-id="element-486" data-mp="1e0sumxasyeoa">{state.sub}</span>}</>} data-id="element-485" data-mp="101kspwn98sge" />
      <div className="tw" data-id="element-487" data-mp="1dyq48c5qmjj1">
        <div className="sub" data-id="element-488" data-mp="191pv1sdld0np">
          In-progress load, pipeline GNARR, and deals closed. Click a region row to drill into SC Leaders view.
        </div>
        <table className="tbl" data-id="element-489" data-mp="lcohxrttd1c3">
          <thead data-id="element-490" data-mp="gd271p1gjx6g">
            <tr data-id="element-491" data-mp="gdkc9b63llqi">
              <th style={{
              width: '14%'
            }} data-id="element-492" data-mp="gdtu2bgzoml4">Region</th>
              <th style={{
              width: '26%'
            }} data-id="element-493" data-mp="gb7m4aq6s52d">In-progress</th>
              <th style={{
              width: '16%'
            }} data-id="element-494" data-mp="v2q7ko1tert4">Pipeline GNARR</th>
              <th style={{
              width: '12%'
            }} data-id="element-495" data-mp="qbhy5s0hj6fy">Closed</th>
              <th style={{
              width: '12%'
            }} data-id="element-496" data-mp="q9x3m2e67ni3">Total</th>
              <th style={{
              width: '10%'
            }} data-id="element-497" data-mp="2l427qes4ng3">On hold</th>
              <th style={{
              width: '10%'
            }} data-id="element-498" data-mp="2jkoa6gliohh">Sub-region</th>
            </tr>
          </thead>
          <tbody data-id="element-499" data-mp="3tros36t3lpbj">
            {regRows.map(([reg, d]) => <tr key={reg} onClick={() => onDrillRegion(d.parent || reg, showSub ? reg : '')} data-id="element-500" data-mp="299nwyw937wcw">
                <td data-id="element-501" data-mp="2e0w8bxtz9rsp">
                  <span className="rdot" style={{
                background: RDOTS[reg] || '#888'
              }} data-id="element-502" data-mp="2e1eblef256a4" />
                  {reg || 'No DR'}
                </td>
                <td data-id="element-503" data-mp="24at7jbgm79jt">
                  <div className="pbg" style={{
                width: 90
              }} data-id="element-504" data-mp="24abf6px2pfh6">
                    <div className="pf" style={{
                  width: `${Math.round(d.ip / maxIP * 100)}%`
                }} data-id="element-505" data-mp="248qfklvwj6cg" />
                  </div>
                  <strong data-id="element-506" data-mp="2wz8k7rc9xfrz">{d.ip}</strong>
                </td>
                <td data-id="element-507" data-mp="2wypct4k929s7">{fmtG(d.gnarr)}</td>
                <td style={{
              color: d.closed > 0 ? '#15803D' : '#344054',
              fontWeight: d.closed > 0 ? 600 : 400
            }} data-id="element-508" data-mp="2izzejllp2429">{d.closed}</td>
                <td data-id="element-509" data-mp="2nxr8el5wg093">{d.book}</td>
                <td data-id="element-510" data-mp="2nwzd7bg7s4jc">{d.onhold}</td>
                <td data-id="element-511" data-mp="17ktxtdoqa4zn">{showSub ? <span className="badge b-gray" style={{
                fontSize: 8
              }} data-id="element-512" data-mp="17k0ygxs3mhrj">{d.parent}</span> : '—'}</td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Book status + CQ grid */}
      {/* <div className="br2">
        <div className="bcard">
          <div className="sh" style={{ padding: '0 0 8px' }}>
            <div className="shb" />
            <span className="sht">Book status mix</span>
          </div>
          <div className="book-bar">
            {segs.map(s => (
              <div key={s.l} style={{ width: `${Math.round(s.v / total * 100)}%`, height: '100%', background: s.c }} />
            ))}
          </div>
          <div className="bleg">
            {segs.map(s => (
              <span key={s.l}>
                <span className="bldot" style={{ background: s.c }} />
                {s.l} {s.v}
              </span>
            ))}
          </div>
        </div>
        <div className="bcard">
          <div className="sh" style={{ padding: '0 0 6px' }}>
            <div className="shb" />
            <span className="sht">In progress by close quarter</span>
          </div>
          <div className="cqg">
            {cqShow.map((q, i) => (
              <div key={q} className="cqc">
                <div className="cql">{q}</div>
                <div className="cqn">{cqMap[q].n}</div>
                <div className="cqs">in-progress deals</div>
                <div className="cqs">{fmtG(cqMap[q].g)} pipeline</div>
                <div className="cqp" style={{ background: qColors[i] || '#4f9cf9' }} />
              </div>
            ))}
          </div>
        </div>
       </div> */}

    </div>;
}